// Bech32 + Bech32m + SegWit address decoder (BIP-173 / BIP-350)

const CHARSET = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
const CHARSET_MAP = new Map();
for (let i = 0; i < CHARSET.length; i++) CHARSET_MAP.set(CHARSET[i], i);

export const BECH32_CHARS = new Set(CHARSET);

const BECH32_CONST = 1;
const BECH32M_CONST = 0x2bc830a3;
const GEN = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];

function polymod(values) {
  let chk = 1;
  for (const v of values) {
    const b = chk >>> 25;
    chk = (((chk & 0x1ffffff) << 5) ^ v) >>> 0;
    for (let i = 0; i < 5; i++) {
      if (((b >>> i) & 1) !== 0) chk ^= GEN[i];
    }
  }
  return chk >>> 0;
}

function hrpExpand(hrp) {
  const out = [];
  for (let i = 0; i < hrp.length; i++) out.push(hrp.charCodeAt(i) >>> 5);
  out.push(0);
  for (let i = 0; i < hrp.length; i++) out.push(hrp.charCodeAt(i) & 31);
  return out;
}

// Decode a Bech32/Bech32m string. Returns { hrp, data, encoding } or null.
export function decodeBech32(addr) {
  if (typeof addr !== "string") return null;
  if (addr.length < 8 || addr.length > 90) return null;

  // Mixed case forbidden
  const lower = addr.toLowerCase();
  const upper = addr.toUpperCase();
  if (addr !== lower && addr !== upper) return null;
  const a = lower;

  const pos = a.lastIndexOf("1");
  if (pos < 1 || pos + 7 > a.length) return null;
  const hrp = a.slice(0, pos);
  for (let i = 0; i < hrp.length; i++) {
    const code = hrp.charCodeAt(i);
    if (code < 33 || code > 126) return null;
  }
  const data = [];
  for (let i = pos + 1; i < a.length; i++) {
    const idx = CHARSET_MAP.get(a[i]);
    if (idx === undefined) return null;
    data.push(idx);
  }
  const chk = polymod([...hrpExpand(hrp), ...data]);
  let encoding;
  if (chk === BECH32_CONST) encoding = "bech32";
  else if (chk === BECH32M_CONST) encoding = "bech32m";
  else return null;
  return { hrp, data: data.slice(0, -6), encoding };
}

function convertBits(data, from, to, pad) {
  let acc = 0;
  let bits = 0;
  const ret = [];
  const maxv = (1 << to) - 1;
  for (const v of data) {
    if (v < 0 || v >>> from) return null;
    acc = ((acc << from) | v) >>> 0;
    bits += from;
    while (bits >= to) {
      bits -= to;
      ret.push((acc >>> bits) & maxv);
    }
  }
  if (pad) {
    if (bits > 0) ret.push((acc << (to - bits)) & maxv);
  } else if (bits >= from || ((acc << (to - bits)) & maxv) !== 0) {
    return null;
  }
  return ret;
}

// Decode a SegWit address (BIP-173 / BIP-350).
// Returns { hrp, version, program } or null.
export function decodeSegwit(addr) {
  const dec = decodeBech32(addr);
  if (!dec) return null;
  if (dec.hrp !== "bc" && dec.hrp !== "tb") return null;
  if (dec.data.length === 0) return null;
  const version = dec.data[0];
  if (version > 16) return null;
  if (version === 0 && dec.encoding !== "bech32") return null;
  if (version > 0 && dec.encoding !== "bech32m") return null;
  const program = convertBits(dec.data.slice(1), 5, 8, false);
  if (!program) return null;
  if (program.length < 2 || program.length > 40) return null;
  if (version === 0 && program.length !== 20 && program.length !== 32) return null;
  return { hrp: dec.hrp, version, program };
}
